import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { twJoin } from 'tailwind-merge';
import { tv, VariantProps } from 'tailwind-variants';
import { defaultInputClasses } from '../utils';

export interface SelectProps extends SelectVariants {
  options?: SelectOption[];
  selected?: SelectOption;
  onChange?: (option: SelectOption) => void;
  prompt?: string;
  disabled?: boolean;
}

export type SelectOption = {
  id: string;
  value: string;
  label: string;
  disabled?: boolean;
};

type SelectVariants = VariantProps<typeof selectVariants>;

const selectVariants = tv({
  slots: {
    button: twJoin(
      'flex w-full group h-10 py-2.5 pl-3 pr-10 bg-gray-100 hover:bg-gray-50 cursor-pointer',
      defaultInputClasses,
    ),
    menu: twJoin(
      'w-full absolute z-20 max-h-60 overflow-auto bg-white py-1 mt-10 shadow-lg transition-all focus:outline-none data-[open]:outline-none',
      'data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in',
    ),
    menuItem: twJoin(
      'group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900',
      'transition-all duration-100 data-[focus]:bg-primary-600 data-[focus]:text-white',
    ),
  },
});

export function Select({
  options = [],
  selected,
  onChange,
  prompt,
  disabled = false,
  ...props
}: SelectProps): JSX.Element {
  const { button, menu, menuItem } = selectVariants(props);

  return (
    <Listbox value={selected || { value: '' }} onChange={onChange} disabled={disabled}>
      <div className="relative font-aribau text-sm w-full flex">
        <ListboxButton className={button()}>
          {selected ? (
            <span className="block truncate">{selected?.label}</span>
          ) : (
            <span className="block truncate text-gray-500">{prompt}</span>
          )}
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ChevronDownIcon
              aria-hidden="true"
              className="h-5 w-5 text-primary-600 group-hover:text-primary-600/70"
            />
          </span>
        </ListboxButton>

        <ListboxOptions transition className={menu()}>
          {options.map((option) => (
            <ListboxOption key={option.value} value={option} className={menuItem()}>
              <span className="block truncate font-normal group-data-[selected]:font-semibold">
                {option.label}
              </span>

              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                <CheckIcon aria-hidden="true" className="size-4" />
              </span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
